import { useEffect, useMemo, useState } from "react";
import { useColorMapping } from "../../hooks/useColorMapping";
import "../../umap.css";
import UmapControls from "./base/umapControls";
import UmapDeckLayer from "./base/umapDeckLayer";
import UmapLegend from "./base/umapLegends";

import { BaseDataPoint } from "./types";

interface UmapVisualizationProps<T extends BaseDataPoint> {
  data: T[];
  title?: string;
}

const UmapVisualization = <T extends BaseDataPoint>({
  data,
}: UmapVisualizationProps<T>) => {
  const [selectedExperiments, setSelectedExperiments] = useState<string[]>([]);

  const experiments = useMemo(() => {
    if (!data || data.length === 0) return [];
    const uniqueExperiments = new Set(data.map((item) => item.experimentId));
    return Array.from(uniqueExperiments).sort();
  }, [data]);

  const filteredData = useMemo(() => {
    if (!selectedExperiments || selectedExperiments.length === 0) return [];
    return data.filter((item) =>
      selectedExperiments.includes(item.experimentId)
    );
  }, [data, selectedExperiments]);

  const allExperimentsForColorMapping = useMemo(
    () => experiments.map((id) => ({ experimentId: id, umap1: 0, umap2: 0 })),
    [experiments]
  );
  const { colorMap } = useColorMapping(
    allExperimentsForColorMapping,
    "experimentId"
  );

  const handleSelectAll = () => setSelectedExperiments(experiments);
  const handleDeselectAll = () => setSelectedExperiments([]);
  const handleLegendItemClick = (experimentId: string) => {
    setSelectedExperiments((prev) =>
      prev.includes(experimentId)
        ? prev.filter((id) => id !== experimentId)
        : [...prev, experimentId]
    );
  };

  useEffect(() => {
    setSelectedExperiments(experiments);
  }, [experiments]);

  if (!data || data.length === 0) {
    return (
      <div className="umap-container">No data available for visualization.</div>
    );
  }
  return (
    <div className="w-full px-8 py-4 mx-auto max-w-7xl">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="z-10 bg-white border rounded shadow-sm lg:col-span-2">
          <UmapDeckLayer
            data={filteredData}
            colorMap={colorMap}
            colorBy={"experimentId"}
          />
        </div>
        <div className="flex flex-col h-full p-4 bg-white border rounded shadow-sm">
          <div className="flex justify-center gap-2 pb-4 mb-4 border-b">
            <UmapControls
              onSelectAll={handleSelectAll}
              onDeselectAll={handleDeselectAll}
            />
          </div>

          <div className="flex-grow overflow-y-auto">
            <UmapLegend
              colorMap={colorMap}
              selectedItems={selectedExperiments}
              onItemClick={handleLegendItemClick}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UmapVisualization;
